import { DESKTOP_MIN_MEDIA } from './layoutConstants';
import {
  DEFAULT_SIDEBAR_COLUMN_WIDTH_PX,
  SIDEBAR_CONTENT_GAP_PX,
  SIDEBAR_RESIZE_HANDLE_PX,
} from './SidebarLayoutContext';

/** Filter column when nothing is stored and the viewport is below `lg`. */
export const TABLET_SIDEBAR_COLUMN_WIDTH_PX = 280;

export const SIDEBAR_COLUMN_MIN_PX = 260;
export const SIDEBAR_COLUMN_MAX_PX = 680;

/**
 * Two-pane pages (search, collection, matches) keep about two gallery tiles
 * beside the filter column.
 */
export const SIDEBAR_CONTENT_MIN_PX = 400;

/** Proxy card pane never shrinks below one tile. */
export const PROXY_GRID_MIN_PX = 200;

export const PROXY_TRAY_MIN_PX = 260;
export const PROXY_TRAY_MAX_PX = 560;
export const PROXY_TRAY_DEFAULT_PX = 320;

/** Resize handle plus the gap before main content. */
export const FILTER_CHROME_PX = SIDEBAR_RESIZE_HANDLE_PX + SIDEBAR_CONTENT_GAP_PX;

/** Resize handle between the proxy tray and the card grid. */
export const TRAY_CHROME_PX = SIDEBAR_RESIZE_HANDLE_PX;

const LG_QUERY = DESKTOP_MIN_MEDIA;

/** Stored filter width when the user has not chosen one yet. Wider from `lg` up. */
export function defaultFilterColumnWidth(): number {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return DEFAULT_SIDEBAR_COLUMN_WIDTH_PX;
  }
  return window.matchMedia(LG_QUERY).matches
    ? DEFAULT_SIDEBAR_COLUMN_WIDTH_PX
    : TABLET_SIDEBAR_COLUMN_WIDTH_PX;
}

/**
 * Auto tray visibility when the user has not chosen. Landscape and desktop
 * fit filters, tray, and two tiles. Portrait does not, unless filters are hidden.
 */
export function proxyTrayDefaultsOpen(rowWidth: number, filtersOpen: boolean): boolean {
  if (rowWidth <= 0) {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(LG_QUERY).matches;
  }
  const need = filtersOpen
    ? SIDEBAR_COLUMN_MIN_PX + PROXY_TRAY_MIN_PX + SIDEBAR_CONTENT_MIN_PX + FILTER_CHROME_PX + TRAY_CHROME_PX
    : PROXY_TRAY_DEFAULT_PX + TRAY_CHROME_PX + SIDEBAR_CONTENT_MIN_PX;
  return rowWidth >= need;
}

export interface PaneFitInput {
  /** Full shell row, including the filter column. 0 before it is measured. */
  rowWidth: number;
  filtersOpen: boolean;
  filterPreferred: number;
  trayRequested: boolean;
  trayPreferred: number;
}

export interface PaneFit {
  filterWidth: number;
  /** Drag cap for the filter column. */
  filterCap: number;
  trayShown: boolean;
  trayWidth: number;
  /** Drag cap for the tray column. */
  trayCap: number;
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.max(min, Math.min(Math.round(value), max));
}

function twoPane(row: number, filtersOpen: boolean, filterPreferred: number): PaneFit {
  if (!filtersOpen) {
    return { filterWidth: 0, filterCap: 0, trayShown: false, trayWidth: 0, trayCap: 0 };
  }
  if (row <= 0) {
    return {
      filterWidth: filterPreferred,
      filterCap: SIDEBAR_COLUMN_MAX_PX,
      trayShown: false,
      trayWidth: 0,
      trayCap: 0,
    };
  }
  const cap = row - FILTER_CHROME_PX - SIDEBAR_CONTENT_MIN_PX;
  const filterCap = Math.min(SIDEBAR_COLUMN_MAX_PX, Math.max(SIDEBAR_COLUMN_MIN_PX, cap));
  return {
    filterWidth: clamp(filterPreferred, SIDEBAR_COLUMN_MIN_PX, filterCap),
    filterCap,
    trayShown: false,
    trayWidth: 0,
    trayCap: 0,
  };
}

/**
 * Share one row between the filter column, the proxy tray, and the card grid.
 * A saved preference is only narrowed for this layout. Drag caps stop either
 * divider from eating the last card tile.
 */
export function fitPanes(input: PaneFitInput): PaneFit {
  const row = Math.max(0, Math.round(input.rowWidth));
  const filterPreferred = clamp(
    input.filterPreferred,
    SIDEBAR_COLUMN_MIN_PX,
    SIDEBAR_COLUMN_MAX_PX,
  );
  const trayPreferred = clamp(input.trayPreferred, PROXY_TRAY_MIN_PX, PROXY_TRAY_MAX_PX);

  if (!input.trayRequested) return twoPane(row, input.filtersOpen, filterPreferred);

  if (row <= 0) {
    return {
      filterWidth: input.filtersOpen ? filterPreferred : 0,
      filterCap: input.filtersOpen ? SIDEBAR_COLUMN_MAX_PX : 0,
      trayShown: true,
      trayWidth: trayPreferred,
      trayCap: PROXY_TRAY_MAX_PX,
    };
  }

  const filterChrome = input.filtersOpen ? FILTER_CHROME_PX : 0;
  let filter = input.filtersOpen ? filterPreferred : 0;
  let tray = trayPreferred;
  const used = () => filter + tray + filterChrome + TRAY_CHROME_PX;

  if (row - used() < PROXY_GRID_MIN_PX) {
    let deficit = PROXY_GRID_MIN_PX - (row - used());
    if (input.filtersOpen) {
      const take = Math.min(deficit, Math.max(0, filter - SIDEBAR_COLUMN_MIN_PX));
      filter -= take;
      deficit -= take;
    }
    const trayTake = Math.min(deficit, Math.max(0, tray - PROXY_TRAY_MIN_PX));
    tray -= trayTake;
    if (row - used() < PROXY_GRID_MIN_PX) return twoPane(row, input.filtersOpen, filterPreferred);
  }

  const slack = Math.max(0, row - used() - PROXY_GRID_MIN_PX);
  const filterCap = input.filtersOpen
    ? Math.min(SIDEBAR_COLUMN_MAX_PX, filter + slack)
    : 0;
  const trayCap = Math.min(PROXY_TRAY_MAX_PX, tray + slack);
  return {
    filterWidth: filter,
    filterCap,
    trayShown: true,
    trayWidth: tray,
    trayCap,
  };
}
